import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

import useErrorHandle from "hooks/useErrorHandle";
import NotFoundPage from "components/error/NotFoundPage";
import PageLayoutSkeleton from "components/PageLayoutSkeleton";
import { getSearchQuery } from "components/SearchInput/helpers";
import { SavedFilterView } from "components/Filters/types";
import usePagination from "components/Table/Pagination/usePagination";
import TablePagination from "components/Table/Pagination";

import StacksPageLayout from "./PageLayout";
import StacksEmpty from "./Empty";
import FiltersLayout from "./FiltersLayout";
import {
  INITIAL_STACKS_LIMIT,
  STACK_LIMIT_STORAGE_KEY,
  ANALYTICS,
  STACK_PAGINATION_OPTIONS,
} from "./constants";
import useStacks from "./useStacks";
import StacksTable from "./Table";

const Stacks = () => {
  const [currentSavedView, setCurrentSavedView] = useState<SavedFilterView | undefined>(undefined);

  const [searchParams] = useSearchParams();
  const searchInput = getSearchQuery(searchParams);

  const {
    initialLoading,
    error,
    stacks,
    loadStacks,
    endCursor,
    startCursor,
    noResults,
    isEmpty,
    loadingContent,
    predicates,
    refetch,
  } = useStacks(STACK_PAGINATION_OPTIONS);

  const [itemsCount, setItemsCount] = useState<number>(0);

  const paginationAnalyticsProps = useMemo(
    () => ({
      rowTotalCount: itemsCount,
    }),
    [itemsCount]
  );

  const {
    rowsPerPage,
    setRowsPerPage,
    from,
    to,
    lastPage,
    page,
    goToNextPage,
    goToPrevPage,
    goToFirstPage,
    goToLastPage,
    resetToInitialPage: resetPaginationToInitialPage,
  } = usePagination({
    ...ANALYTICS,
    analyticsProps: paginationAnalyticsProps,
    fetchItems: loadStacks,
    endCursor,
    startCursor,
    allItemsCount: itemsCount,
    visibleItemsCount: stacks.length,
    initialLimit: INITIAL_STACKS_LIMIT,
    storageKey: STACK_LIMIT_STORAGE_KEY,
  });

  useEffect(() => {
    if (searchParams.size === 0 && page !== 1) {
      goToFirstPage();
    }
  }, [searchParams, page, goToFirstPage]);

  const ErrorContent = useErrorHandle(error);

  if (ErrorContent) {
    return ErrorContent;
  }

  if (initialLoading) {
    return (
      <StacksPageLayout>
        <PageLayoutSkeleton />
      </StacksPageLayout>
    );
  }

  if (noResults) {
    return <NotFoundPage />;
  }

  return (
    <StacksPageLayout searchCallback={resetPaginationToInitialPage}>
      <FiltersLayout
        itemsCount={itemsCount}
        setItemsCount={setItemsCount}
        predicates={predicates}
        currentSavedView={currentSavedView}
        setCurrentSavedView={setCurrentSavedView}
        applyFilterCallback={resetPaginationToInitialPage}
      >
        {isEmpty && <StacksEmpty hasNoResults={!!searchInput || predicates.length > 0} />}
        {!isEmpty && (
          <StacksTable stacks={stacks} refetchVisibleStacks={refetch} loading={loadingContent} />
        )}

        <TablePagination
          from={from}
          to={to}
          loading={loadingContent}
          goToNextPage={goToNextPage}
          setRowsPerPage={setRowsPerPage}
          goToPrevPage={goToPrevPage}
          goToFirstPage={goToFirstPage}
          goToLastPage={goToLastPage}
          rowsPerPage={rowsPerPage}
          itemsCount={itemsCount}
          lastPage={lastPage}
          page={page}
          paginationOptions={STACK_PAGINATION_OPTIONS}
        />
      </FiltersLayout>
    </StacksPageLayout>
  );
};

export default Stacks;
